// Device geolocation helpers — "Where am I?"
// Uses the browser Geolocation API plus OpenStreetMap Nominatim for reverse
// geocoding (free, keyless) to turn coordinates into a readable address.

import type { Coordinates } from '../types';

export interface WhereAmI {
  coords: Coordinates;
  /** e.g. "12.5 m" */
  accuracy: number;
  /** compass heading in degrees, or null when unavailable */
  heading: number | null;
  /** speed in m/s, or null */
  speed: number | null;
  /** free-form address from reverse geocoding, or null */
  address: string | null;
  /** true while a reverse-geocode request is in flight */
  resolvingAddress: boolean;
  /** timestamp of the fix (ms epoch) */
  timestamp: number;
}

export type GeoErrorKind =
  | 'permission-denied'
  | 'position-unavailable'
  | 'timeout'
  | 'unsupported'
  | 'insecure-context'
  | 'unknown';

export class GeoError extends Error {
  kind: GeoErrorKind;
  constructor(kind: GeoErrorKind, message: string) {
    super(message);
    this.kind = kind;
    this.name = 'GeoError';
  }
}

/**
 * Geolocation is a secure-context API. On http://<LAN-IP> browsers hide
 * `navigator.geolocation` entirely, so we must detect that separately from
 * "browser does not support geolocation" — the fix is different (serve over
 * HTTPS) and the message needs to say so.
 */
export function isSecureOrigin(): boolean {
  if (typeof window === 'undefined') return true;
  // file:// and localhost are treated as secure by browsers
  if (window.isSecureContext) return true;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

export function isGeolocationSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'geolocation' in navigator &&
    typeof navigator.geolocation.getCurrentPosition === 'function'
  );
}

/** Why geolocation cannot be used right now, if it cannot. */
export function geolocationUnavailableReason(): GeoError | null {
  if (typeof navigator === 'undefined') {
    return new GeoError('unsupported', 'Geolocation is unavailable in this environment.');
  }
  if (!isSecureOrigin()) {
    return new GeoError(
      'insecure-context',
      `Your browser blocks location on http://${window.location.hostname}. ` +
        `Open the app on https:// or on localhost instead.`
    );
  }
  if (!isGeolocationSupported()) {
    return new GeoError('unsupported', 'This browser does not provide geolocation.');
  }
  return null;
}

/** Map a GeolocationPositionError code to our own kind. */
function toGeoError(err: GeolocationPositionError): GeoError {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return new GeoError(
        'permission-denied',
        'Location permission denied. Enable it in your browser settings to use "Where am I".'
      );
    case err.POSITION_UNAVAILABLE:
      return new GeoError(
        'position-unavailable',
        'Could not determine your location. Try again in a moment.'
      );
    case err.TIMEOUT:
      return new GeoError('timeout', 'Location request timed out. Please try again.');
    default:
      return new GeoError('unknown', err.message || 'Unknown location error.');
  }
}

export interface GetPositionOptions {
  highAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

/**
 * How trustworthy is this fix?
 *
 * A desktop browser has no GPS, so the Geolocation API silently falls back to
 * network sources (Wi-Fi databases, cell towers, IP geolocation). Those can be
 * hundreds of metres out — or, for IP geolocation, the wrong city entirely —
 * while still returning a `coords` object that looks perfectly valid. The
 * accuracy radius is the only honest signal of which source was used, so we
 * surface it instead of pretending the position is exact.
 */
export type FixQuality = 'gps' | 'network' | 'coarse' | 'unknown';

export function classifyFix(accuracy: number): FixQuality {
  if (!Number.isFinite(accuracy) || accuracy <= 0) return 'unknown';
  if (accuracy <= 50) return 'gps';
  if (accuracy <= 2000) return 'network';
  return 'coarse';
}

/** Short, honest explanation for a fix that is not GPS-grade. */
export function fixQualityHint(accuracy: number): string | null {
  switch (classifyFix(accuracy)) {
    case 'gps':
      return null;
    case 'network':
      return 'This is a network-based position (Wi-Fi or cell tower), not GPS, so it can be off by up to '
        + `${Math.round(accuracy)} m. It improves outdoors or on a phone.`;
    case 'coarse':
      return 'This is a coarse IP-based position, so it can be off by several kilometres. '
        + 'It will not match your exact spot on desktop.';
    default:
      return null;
  }
}

/** One-shot position read. */
export function getCurrentPosition(
  opts: GetPositionOptions = {}
): Promise<WhereAmI> {
  return new Promise((resolve, reject) => {
    const pre = geolocationUnavailableReason();
    if (pre) {
      reject(pre);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          coords: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          },
          accuracy: pos.coords.accuracy ?? 0,
          heading: pos.coords.heading ?? null,
          speed: pos.coords.speed ?? null,
          address: null,
          resolvingAddress: true,
          timestamp: pos.timestamp,
        }),
      (err) => reject(toGeoError(err)),
      {
        enableHighAccuracy: opts.highAccuracy ?? true,
        timeout: opts.timeout ?? 12_000,
        maximumAge: opts.maximumAge ?? 0,
      }
    );
  });
}

/** Live position subscription. Returns an unsubscribe function. */
export function watchPosition(
  onUpdate: (value: WhereAmI) => void,
  onError: (error: GeoError) => void,
  opts: GetPositionOptions = {}
): () => void {
  const pre = geolocationUnavailableReason();
  if (pre) {
    onError(pre);
    return () => {};
  }

  const id = navigator.geolocation.watchPosition(
    (pos) =>
      onUpdate({
        coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        accuracy: pos.coords.accuracy ?? 0,
        heading: pos.coords.heading ?? null,
        speed: pos.coords.speed ?? null,
        address: null,
        resolvingAddress: true,
        timestamp: pos.timestamp,
      }),
    (err) => onError(toGeoError(err)),
    {
      enableHighAccuracy: opts.highAccuracy ?? true,
      timeout: opts.timeout ?? 20_000,
      // A cached fix can come from a much coarser source than the one the
      // browser would use now, which is a common cause of the dot sitting on
      // the wrong block. Ask for something close to a fresh computation.
      maximumAge: opts.maximumAge ?? 1_000,
    }
  );

  return () => navigator.geolocation.clearWatch(id);
}

/* ------------------------------------------------------------------ */
/*  Reverse geocoding (OpenStreetMap Nominatim — free, no key)        */
/* ------------------------------------------------------------------ */

const NOMINATIM = 'https://nominatim.openstreetmap.org/reverse';

export interface ReverseGeocodeOptions {
  signal?: AbortSignal;
  zoom?: number;
}

/** Turn coordinates into a short human-readable address. */
export async function reverseGeocode(
  coords: Coordinates,
  opts: ReverseGeocodeOptions = {}
): Promise<string | null> {
  const params = new URLSearchParams({
    format: 'jsonv2',
    lat: String(coords.lat),
    lon: String(coords.lng),
    zoom: String(opts.zoom ?? 18),
    addressdetails: '1',
  });

  try {
    const res = await fetch(`${NOMINATIM}?${params.toString()}`, {
      signal: opts.signal,
      headers: { 'User-Agent': 'LeadMap3D/1.0 (open source map app)' },
    });

    if (!res.ok) return null;

    const data = await res.json();
    const a = data?.address ?? {};

    // Prefer the most specific components available
    const parts = [
      a.house_number,
      a.road,
      a.neighbourhood,
      a.suburb,
      a.city ?? a.town ?? a.village ?? a.municipality,
      a.state,
    ].filter(Boolean);

    if (parts.length) return parts.join(', ');

    return data?.display_name ? String(data.display_name).split(',').slice(0, 3).join(',').trim() : null;
  } catch {
    return null;
  }
}

/** Forward geocode a free-text place into coordinates (used by the route panel). */
export async function forwardGeocode(
  query: string,
  signal?: AbortSignal
): Promise<{ coords: Coordinates; label: string }[]> {
  const params = new URLSearchParams({
    format: 'jsonv2',
    q: query,
    limit: '5',
    addressdetails: '1',
  });

  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
    {
      signal,
      headers: { 'User-Agent': 'LeadMap3D/1.0 (open source map app)' },
    }
  );

  if (!res.ok) return [];

  const data = await res.json();
  if (!Array.isArray(data)) return [];

  return data.map((r: any) => ({
    coords: { lat: parseFloat(r.lat), lng: parseFloat(r.lon) },
    label: r.display_name as string,
  }));
}

export function formatAccuracy(meters: number): string {
  if (!Number.isFinite(meters)) return '—';
  if (meters < 1000) return `±${Math.round(meters)} m`;
  return `±${(meters / 1000).toFixed(1)} km`;
}

export function formatSpeed(metersPerSecond: number | null): string {
  if (metersPerSecond == null) return '—';
  const kmh = metersPerSecond * 3.6;
  if (kmh < 1) return 'stationary';
  return `${kmh.toFixed(1)} km/h`;
}